import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent } from "@repo/ui/card";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  fetchLatestPropertySuburbPv,
  fetchValuationRunResults,
  findPropertySuburb,
  runMonthlyMovement,
  type Property,
} from "@/lib/api";
import { indexLastSale } from "@/lib/propertyDetail";
import { formatDate } from "@/lib/formatDate";

const money = (value: number) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(value);

export function PropertyPvReport({ property }: { property: Property }) {
  const { canManageJobs } = useAuth();
  const client = useQueryClient();
  const suburb = useQuery({
    queryKey: ["property-pv-suburb", property.suburb, property.state, property.postcode],
    queryFn: () => findPropertySuburb(property),
  });
  const run = useQuery({
    queryKey: ["property-pv-run", suburb.data],
    queryFn: () => fetchLatestPropertySuburbPv(suburb.data as string),
    enabled: !!suburb.data,
  });
  const results = useQuery({
    queryKey: ["property-pv-results", run.data?.id],
    queryFn: () => fetchValuationRunResults(run.data?.id as string),
    enabled: !!run.data?.id,
  });
  const generate = useMutation({
    mutationFn: () => runMonthlyMovement({ suburbId: suburb.data as string }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["property-pv-run", suburb.data] });
      await run.refetch();
    },
  });
  const months = results.data ?? [];
  const latest = months.at(-1);
  const house = /^(house|detached)/i.test(property.property_type ?? "");
  const estimate =
    house && run.data?.property_type?.toLowerCase() === "house"
      ? indexLastSale(property.last_sale_price, property.last_sale_date, months)
      : null;
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold">Present value report</h2>
            <p className="text-xs text-muted-foreground">
              Suburb-level monthly house sale evidence for {property.suburb}, {property.state}{" "}
              {property.postcode}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            disabled={!canManageJobs || !suburb.data || generate.isPending}
            title={!canManageJobs ? "Operator role required" : undefined}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? "Generating…" : "Generate PV report"}
          </Button>
        </div>
        {suburb.isLoading || run.isLoading || results.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading suburb PV…</p>
        ) : null}
        {!suburb.data && !suburb.isLoading && (
          <p className="text-sm text-muted-foreground">
            No unique matching suburb record for this property's suburb, state and postcode. PV
            cannot run.
          </p>
        )}
        {(suburb.error || run.error || results.error || generate.error) && (
          <p className="text-sm text-destructive">
            PV unavailable: {String(suburb.error ?? run.error ?? results.error ?? generate.error)}
          </p>
        )}
        {suburb.data && !run.data && !run.isLoading && (
          <p className="text-sm text-muted-foreground">
            No completed suburb PV run yet. Generate one to see measured monthly results.
          </p>
        )}
        {run.data && (
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Suburb-level estimate</Badge>
              <Badge variant="outline">{run.data.property_type} sales</Badge>
              <span className="text-muted-foreground">
                Run {formatDate(run.data.completed_at, { includeTime: true })}
              </span>
            </div>
            {latest ? (
              <p>
                Latest measured month: <strong>{latest.sale_month.slice(0, 7)}</strong> · Average{" "}
                {latest.average_price == null ? "unavailable" : money(latest.average_price)} ·
                Median {latest.median_price == null ? "unavailable" : money(latest.median_price)} ·{" "}
                {latest.sample_count} sales
              </p>
            ) : (
              <p>No monthly results were produced by this run.</p>
            )}
            <p>
              Property last sale:{" "}
              {property.last_sale_price && property.last_sale_date
                ? `${money(property.last_sale_price)} on ${formatDate(property.last_sale_date)}`
                : "unavailable"}
              .
            </p>
            {estimate != null && latest ? (
              <p className="font-medium">
                Indexed last sale: {money(estimate)}{" "}
                <span className="font-normal text-muted-foreground">
                  (suburb-level estimate at {latest.sale_month.slice(0, 7)})
                </span>
              </p>
            ) : (
              <p className="text-muted-foreground">
                Indexed property figure unavailable: requires a house sale with a price and a
                complete chain of measured suburb house months from its sale month to the latest
                month.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Method: last sale price × latest suburb average house sale price ÷ suburb average in
              the property's sale month. This transfers observed suburb movement to this address; it
              is not a property appraisal. Months without sales break the chain.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
